import { Button, Input, Label, TextArea, TextField } from "@heroui/react";
import type { CreateLocationInput } from "@storyline/shared";
import { useState } from "react";

type LocationFormProps = {
	initial?: CreateLocationInput;
	submitLabel: string;
	onSubmit: (body: CreateLocationInput) => Promise<void>;
	onCancel?: () => void;
};

const emptyToNull = (value: string): string | null => (value.trim() === "" ? null : value);

const LocationForm: React.FC<LocationFormProps> = ({ initial, submitLabel, onSubmit, onCancel }) => {
	const [name, setName] = useState(initial?.name ?? "");
	const [description, setDescription] = useState(initial?.description ?? "");
	const [lighting, setLighting] = useState(initial?.lighting ?? "");
	const [palette, setPalette] = useState(initial?.palette ?? "");
	const [cameraNotes, setCameraNotes] = useState(initial?.cameraNotes ?? "");
	const [ambienceNotes, setAmbienceNotes] = useState(initial?.ambienceNotes ?? "");
	const [busy, setBusy] = useState(false);

	const submit = async (): Promise<void> => {
		if (name.trim() === "") {
			return;
		}
		setBusy(true);
		try {
			await onSubmit({
				name,
				description,
				lighting: emptyToNull(lighting),
				palette: emptyToNull(palette),
				cameraNotes: emptyToNull(cameraNotes),
				ambienceNotes: emptyToNull(ambienceNotes),
			});
		} finally {
			setBusy(false);
		}
	};

	return (
		<div className="flex flex-col gap-3 rounded-xl border border-border-soft bg-glass p-4">
			<TextField value={name} onChange={setName}>
				<Label>Nom</Label>
				<Input placeholder="Le hangar" />
			</TextField>
			<TextField value={description} onChange={setDescription}>
				<Label>Description</Label>
				<TextArea placeholder="Hangar désaffecté, tôle rouillée…" rows={2} />
			</TextField>
			<div className="grid gap-3 md:grid-cols-2">
				<TextField value={lighting} onChange={setLighting}>
					<Label>Lumière (optionnel)</Label>
					<Input placeholder="Néons verts vacillants" />
				</TextField>
				<TextField value={palette} onChange={setPalette}>
					<Label>Palette (optionnel)</Label>
					<Input placeholder="Cyan, rouille, noir" />
				</TextField>
				<TextField value={cameraNotes} onChange={setCameraNotes}>
					<Label>Notes caméra (optionnel)</Label>
					<Input placeholder="Grand angle, contre-plongée" />
				</TextField>
				<TextField value={ambienceNotes} onChange={setAmbienceNotes}>
					<Label>Ambiance sonore (optionnel, audio non produit)</Label>
					<Input placeholder="Gouttes d'eau, néon qui grésille" />
				</TextField>
			</div>
			<div className="flex justify-end gap-2">
				{onCancel ? (
					<Button size="sm" variant="ghost" onPress={onCancel} className="text-muted">
						Annuler
					</Button>
				) : null}
				<Button size="sm" isDisabled={busy || name.trim() === ""} onPress={() => void submit()}>
					{busy ? "…" : submitLabel}
				</Button>
			</div>
		</div>
	);
};

export default LocationForm;
